import type { StageInfo } from '../types';

interface StageProgressProps {
  stages: StageInfo[];
  isFail?: boolean;
  failStageName?: string;
}

/**
 * Визуальный прогресс-бар стадий воронки
 * Показывает подписи ко всем стадиям, с горизонтальным скроллом если стадий > 5
 */
export function StageProgress({ stages, isFail, failStageName }: StageProgressProps) {
  if (!stages || stages.length === 0) return null;

  const needsScroll = stages.length > 5;

  return (
    <div style={{ padding: '16px' }}>
      {/* Прогресс-бар с горизонтальным скроллом */}
      <div
        style={{
          overflowX: needsScroll ? 'auto' : 'visible',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: needsScroll ? '4px' : '0',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '2px',
            minWidth: needsScroll ? `${stages.length * 70}px` : undefined,
          }}
        >
          {stages.map((stage, index) => {
            let barColor: string;
            let labelColor: string;

            if (isFail) {
              barColor = '#e0e0e0';
              labelColor = '#999';
            } else if (stage.isPassed) {
              barColor = '#4caf50';
              labelColor = '#4caf50';
            } else if (stage.isCurrent) {
              barColor = '#2196f3';
              labelColor = '#2196f3';
            } else {
              barColor = '#e0e0e0';
              labelColor = '#999';
            }

            return (
              <div
                key={stage.id}
                style={{
                  flex: needsScroll ? '0 0 auto' : 1,
                  width: needsScroll ? '68px' : undefined,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {/* Сегмент полоски */}
                <div
                  style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: barColor,
                    borderRadius: index === 0 ? '2px 0 0 2px' : index === stages.length - 1 ? '0 2px 2px 0' : '0',
                    position: 'relative',
                  }}
                >
                  {/* Пульсация для текущей стадии */}
                  {stage.isCurrent && !isFail && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-3px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: '#2196f3',
                        boxShadow: '0 0 0 3px rgba(33, 150, 243, 0.3)',
                      }}
                    />
                  )}
                </div>

                {/* Иконка */}
                <span style={{ fontSize: '10px' }}>
                  {isFail ? '—' : stage.isPassed ? '✅' : stage.isCurrent ? '🔵' : '⚪'}
                </span>

                {/* Название — всегда показываем для всех стадий */}
                <span
                  style={{
                    fontSize: '10px',
                    color: labelColor,
                    textAlign: 'center',
                    lineHeight: '1.2',
                    maxWidth: '80px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    fontWeight: stage.isCurrent ? 600 : 400,
                  }}
                >
                  {stage.clientLabel || stage.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Провальная стадия — красный индикатор */}
      {isFail && failStageName && (
        <div
          style={{
            marginTop: '12px',
            padding: '8px 12px',
            backgroundColor: '#fff0f0',
            borderRadius: '8px',
            borderLeft: '3px solid #ff3b30',
            fontSize: '13px',
            color: '#ff3b30',
          }}
        >
          {failStageName}
        </div>
      )}
    </div>
  );
}
